import { supabase } from '../lib/supabase';

export const dataManagementService = {
  /**
   * Delete all referrals for a specific practitioner (Endodontist only)
   * @param {string} practitionerId - The practitioner's ID
   * @returns {Promise<{success: boolean, deletedCount: number}>}
   */
  async deletePractitionerReferrals(practitionerId) {
    try {
      if (!practitionerId) {
        throw new Error('practitioner_id est requis');
      }

      // First, count how many referrals will be deleted
      const { count, error: countError } = await supabase
        ?.from('referrals')?.select('*', { count: 'exact', head: true })
        ?.eq('practitioner_id', practitionerId);

      if (countError) throw countError;

      // Delete all referrals for this practitioner
      const { error: deleteError } = await supabase
        ?.from('referrals')?.delete()?.eq('practitioner_id', practitionerId);

      if (deleteError) throw deleteError;

      return {
        success: true,
        deletedCount: count || 0
      };
    } catch (error) {
      console.error('Error deleting practitioner referrals:', error);
      throw new Error(`Erreur lors de la suppression: ${error?.message}`);
    }
  },

  /**
   * Delete ALL data from the database (Super Admin only)
   * Deletes all referrals, then all practitioners (except those with user accounts)
   * @returns {Promise<{success: boolean, deletedReferrals: number, deletedPractitioners: number}>}
   */
  async deleteAllData() {
    try {
      // Step 1: Count and delete all referrals
      const { count: referralCount, error: referralCountError } = await supabase
        ?.from('referrals')
        ?.select('*', { count: 'exact', head: true });

      if (referralCountError) throw referralCountError;

      const { error: deleteReferralsError } = await supabase
        ?.from('referrals')
        ?.delete()
        ?.neq('id', '00000000-0000-0000-0000-000000000000'); // Delete all (using impossible condition to delete everything)

      if (deleteReferralsError) throw deleteReferralsError;

      // Step 2: Get practitioners that can be safely deleted (those without user accounts)
      const { data: userProfiles, error: userProfilesError } = await supabase
        ?.from('user_profiles')
        ?.select('practitioner_id');

      if (userProfilesError) throw userProfilesError;

      const practitionerIdsWithAccounts = userProfiles
        ?.filter(up => up?.practitioner_id)
        ?.map(up => up?.practitioner_id) || [];

      // Step 3: Count practitioners that will be deleted
      let practitionerDeleteQuery = supabase
        ?.from('practitioners')
        ?.select('*', { count: 'exact', head: true });

      if (practitionerIdsWithAccounts?.length > 0) {
        practitionerDeleteQuery = practitionerDeleteQuery?.not('id', 'in', `(${practitionerIdsWithAccounts?.join(',')})`);
      }

      const { count: practitionerCount, error: practitionerCountError } = await practitionerDeleteQuery;

      if (practitionerCountError) throw practitionerCountError;

      // Step 4: Delete practitioners without user accounts
      if (practitionerIdsWithAccounts?.length > 0) {
        const { error: deletePractitionersError } = await supabase
          ?.from('practitioners')
          ?.delete()
          ?.not('id', 'in', `(${practitionerIdsWithAccounts?.join(',')})`);

        if (deletePractitionersError) throw deletePractitionersError;
      } else {
        // If no practitioners have accounts, delete all
        const { error: deletePractitionersError } = await supabase
          ?.from('practitioners')
          ?.delete()
          ?.neq('id', '00000000-0000-0000-0000-000000000000');

        if (deletePractitionersError) throw deletePractitionersError;
      }

      return {
        success: true,
        deletedReferrals: referralCount || 0,
        deletedPractitioners: practitionerCount || 0
      };
    } catch (error) {
      console.error('Error deleting all data:', error);
      throw new Error(`Erreur lors de la suppression complète: ${error?.message}`);
    }
  }
};